import React, { useState, useCallback } from 'react';
import { AdminSettings, LoginBanner, LoginPageFooter } from '../../types';

const FONT_OPTIONS = [
  { value: 'system', label: 'System default' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Sora', label: 'Sora' },
];

const MAX_IMAGE_BYTES = 400 * 1024; // 400KB for data URL
const MAX_FOOTER_IMAGE_BYTES = 500 * 1024; // 500KB for footer image

function generateId(): string {
  return `banner-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface LoginPageTabProps {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => Promise<unknown>;
}

export const LoginPageTab: React.FC<LoginPageTabProps> = ({ settings, setSettings, saveSettings }) => {
  const banners = settings.loginPage?.banners ?? [];
  const footer = settings.loginPage?.footer ?? { visible: true, leftText: 'Miracle Experience — Guest Experience Intelligence', rightText: 'Secure access · Enterprise node' };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const setBanners = useCallback(
    (next: LoginBanner[]) => {
      setSettings({
        ...settings,
        loginPage: { ...settings.loginPage, banners: next, footer },
      });
    },
    [settings, setSettings, footer]
  );

  const setFooter = useCallback(
    (next: LoginPageFooter) => {
      setSettings({
        ...settings,
        loginPage: { ...settings.loginPage, banners, footer: next },
      });
    },
    [settings, setSettings, banners]
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    try {
      const next = { ...settings, loginPage: { banners, footer } };
      await saveSettings(next);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [settings, banners, footer, saveSettings]);

  const addBanner = () => {
    const newBanner: LoginBanner = {
      id: generateId(),
      enabled: true,
      order: banners.length,
      imageUrl: '',
      overlayColor: '#000000',
      overlayOpacity: 0.35,
      textColor: '#ffffff',
      fontFamily: 'Montserrat',
      titleFontSize: 28,
      subtitleFontSize: 14,
    };
    setBanners([...banners, newBanner]);
    setEditingId(newBanner.id);
  };

  const updateBanner = (id: string, updates: Partial<LoginBanner>) => {
    setBanners(
      banners.map((b) => (b.id === id ? { ...b, ...updates } : b))
    );
  };

  const removeBanner = (id: string) => {
    setBanners(banners.filter((b) => b.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const moveBanner = (id: string, direction: 'up' | 'down') => {
    const idx = banners.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const next = [...banners];
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    next.forEach((b, i) => updateBanner(b.id, { order: i }));
    setBanners(next.map((b, i) => ({ ...b, order: i })));
  };

  const editingBanner = editingId ? banners.find((b) => b.id === editingId) : null;

  const handleImageChange = (id: string, file: File | null) => {
    if (!file) {
      updateBanner(id, { imageUrl: '' });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert(`Image should be under ${MAX_IMAGE_BYTES / 1024}KB. This file is ${(file.size / 1024).toFixed(0)}KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateBanner(id, { imageUrl: reader.result as string });
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Login Page Banners</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Control images, colors, and text shown on the login screen. Banners appear in the background; reorder and toggle on/off.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'saved' && (
            <span className="text-sm font-medium text-emerald-600">Saved</span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm font-medium text-rose-600">Save failed</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold shadow-lg shadow-blue-500/30 hover:shadow-xl disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={addBanner}
            className="px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 hover:border-indigo-400 hover:text-indigo-600 text-sm font-bold transition-colors"
          >
            + Add banner
          </button>
        </div>
      </div>

      {/* Footer (bottom of login page): text bar and/or uploadable image strip, optional animation */}
      <div className="rounded-2xl border-2 border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50/20 p-6">
        <h4 className="text-base font-bold text-slate-800 mb-4">Login page footer</h4>
        <p className="text-sm text-slate-500 mb-4">Use the big space at the bottom: text bar and/or a full-width image (e.g. branded strip, animated GIF). Upload an image to fill that area.</p>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={footer?.visible !== false}
              onChange={(e) => setFooter({ ...footer, visible: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600"
            />
            <span className="text-sm font-medium text-slate-700">Show footer</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={footer?.animated === true}
              onChange={(e) => setFooter({ ...footer, animated: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600"
            />
            <span className="text-sm font-medium text-slate-700">Animated (subtle motion)</span>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Footer image (optional, max 500KB — fills the bottom area; GIF = animated)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) {
                setFooter({ ...footer, imageUrl: undefined });
                return;
              }
              if (file.size > MAX_FOOTER_IMAGE_BYTES) {
                alert(`Image must be under ${MAX_FOOTER_IMAGE_BYTES / 1024}KB. This file is ${(file.size / 1024).toFixed(0)}KB.`);
                return;
              }
              const reader = new FileReader();
              reader.onload = () => setFooter({ ...footer, imageUrl: reader.result as string });
              reader.readAsDataURL(file);
            }}
            className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          {footer?.imageUrl && (
            <div className="mt-2 flex items-center gap-2">
              <img src={footer.imageUrl} alt="Footer preview" className="h-14 object-cover rounded-lg border border-slate-200" />
              <button type="button" onClick={() => setFooter({ ...footer, imageUrl: undefined })} className="text-xs font-bold text-rose-600 hover:underline">Remove image</button>
            </div>
          )}
        </div>
        <div className="mb-4">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Footer min height (px) — image fills rest to bottom</label>
          <input
            type="number"
            min={60}
            max={400}
            value={footer?.imageHeightPx ?? 140}
            onChange={(e) => setFooter({ ...footer, imageHeightPx: Math.min(400, Math.max(60, parseInt(e.target.value, 10) || 140)) })}
            className="w-32 px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <p className="text-xs text-slate-500 mt-1">With an image, the footer expands to fill all space to the bottom of the page.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Left text (overlay on image or in bar)</label>
            <input
              type="text"
              value={footer?.leftText ?? ''}
              onChange={(e) => setFooter({ ...footer, leftText: e.target.value })}
              placeholder="e.g. Miracle Air — Aviation Intelligence"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Right text</label>
            <input
              type="text"
              value={footer?.rightText ?? ''}
              onChange={(e) => setFooter({ ...footer, rightText: e.target.value })}
              placeholder="e.g. Secure access · Enterprise node"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {banners.map((b) => (
          <div
            key={b.id}
            className={`rounded-2xl border-2 overflow-hidden transition-all ${
              editingId === b.id
                ? 'border-indigo-500 ring-2 ring-indigo-200'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div
              className="aspect-video bg-slate-100 relative flex items-center justify-center"
              style={{
                background: b.imageUrl
                  ? `center/cover no-repeat url(${b.imageUrl})`
                  : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
              }}
            >
              {!b.imageUrl && (
                <span className="text-slate-400 text-xs font-medium">No image</span>
              )}
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: b.overlayColor ?? '#000000',
                  opacity: b.overlayOpacity ?? 0.35,
                }}
              />
              {(b.title || b.subtitle) && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center"
                  style={{
                    color: b.textColor ?? '#ffffff',
                    fontFamily: b.fontFamily && b.fontFamily !== 'system' ? b.fontFamily : undefined,
                  }}
                >
                  {b.title && (
                    <span style={{ fontSize: Math.min(20, b.titleFontSize ?? 28) }} className="font-bold">
                      {b.title}
                    </span>
                  )}
                  {b.subtitle && (
                    <span style={{ fontSize: Math.min(12, b.subtitleFontSize ?? 14) }} className="mt-1 opacity-90">
                      {b.subtitle}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 flex items-center justify-between bg-white border-t border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={b.enabled}
                  onChange={(e) => updateBanner(b.id, { enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                />
                <span className="text-xs font-medium text-slate-600">Enabled</span>
              </label>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveBanner(b.id, 'up')}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title="Move up"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => moveBanner(b.id, 'down')}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  title="Move down"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setEditingId(editingId === b.id ? null : b.id)}
                  className="px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                >
                  {editingId === b.id ? 'Done' : 'Edit'}
                </button>
                <button
                  type="button"
                  onClick={() => removeBanner(b.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  title="Remove"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingBanner && (
        <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-slate-50 to-indigo-50/30 p-6 shadow-lg">
          <h4 className="text-base font-bold text-slate-800 mb-4">Edit banner</h4>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Image (max 400KB)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(editingBanner.id, e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Link URL (optional)
                </label>
                <input
                  type="url"
                  value={editingBanner.linkUrl ?? ''}
                  onChange={(e) => updateBanner(editingBanner.id, { linkUrl: e.target.value || undefined })}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={editingBanner.title ?? ''}
                  onChange={(e) => updateBanner(editingBanner.id, { title: e.target.value || undefined })}
                  placeholder="Headline"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={editingBanner.subtitle ?? ''}
                  onChange={(e) => updateBanner(editingBanner.id, { subtitle: e.target.value || undefined })}
                  placeholder="Subtext"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Overlay color
                  </label>
                  <input
                    type="color"
                    value={editingBanner.overlayColor ?? '#000000'}
                    onChange={(e) => updateBanner(editingBanner.id, { overlayColor: e.target.value })}
                    className="w-full h-10 rounded-xl border border-slate-200 cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Text color
                  </label>
                  <input
                    type="color"
                    value={editingBanner.textColor ?? '#ffffff'}
                    onChange={(e) => updateBanner(editingBanner.id, { textColor: e.target.value })}
                    className="w-full h-10 rounded-xl border border-slate-200 cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Overlay opacity: {(editingBanner.overlayOpacity ?? 0.35).toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={editingBanner.overlayOpacity ?? 0.35}
                  onChange={(e) => updateBanner(editingBanner.id, { overlayOpacity: parseFloat(e.target.value) })}
                  className="w-full h-2 rounded-lg appearance-none bg-slate-200 accent-indigo-600"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Font family
                </label>
                <select
                  value={editingBanner.fontFamily ?? 'system'}
                  onChange={(e) => updateBanner(editingBanner.id, { fontFamily: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Title size (px)
                  </label>
                  <input
                    type="number"
                    min="12"
                    max="72"
                    value={editingBanner.titleFontSize ?? 28}
                    onChange={(e) =>
                      updateBanner(editingBanner.id, { titleFontSize: parseInt(e.target.value, 10) || 28 })
                    }
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Subtitle size (px)
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="36"
                    value={editingBanner.subtitleFontSize ?? 14}
                    onChange={(e) =>
                      updateBanner(editingBanner.id, { subtitleFontSize: parseInt(e.target.value, 10) || 14 })
                    }
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {banners.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-12 text-center">
          <p className="text-slate-500 font-medium">No banners yet</p>
          <p className="text-sm text-slate-400 mt-1">Add a banner to show images or promotions on the login page.</p>
          <button
            type="button"
            onClick={addBanner}
            className="mt-4 px-5 py-2.5 rounded-xl bg-indigo-100 text-indigo-700 font-bold text-sm hover:bg-indigo-200"
          >
            + Add first banner
          </button>
        </div>
      )}
    </div>
  );
};
